/**
 * "Reduce motion" preference — a leaf module with no imports of its own.
 * main.ts calls applyReduceMotion() as its very first statement (before
 * anything else initializes), and the Settings screen calls the same
 * functions when the toggle is mounted/flipped. Keeping this dependency-free
 * means main.ts can apply the stored preference without importing anything
 * that (transitively, once settingsTab is wired into engine/bootstrap.ts's
 * GAME_TABS) would import main.ts back — no cycle, one source of truth for
 * the storage key.
 *
 * The applied value is a `data-reduce-motion` attribute on <html>, which
 * app.css keys a `[data-reduce-motion='true']` rule off (see that file for
 * exactly what it disables).
 */

export const REDUCE_MOTION_KEY = 'reduceMotion';

/** Reads the persisted preference. Defaults to false (motion on) when unset
 * — this is a deliberate, explicit opt-in toggle, not an OS-preference mirror. */
export function isReduceMotionEnabled(): boolean {
  try {
    return localStorage.getItem(REDUCE_MOTION_KEY) === 'true';
  } catch {
    // Storage blocked (private-browsing lockdown, disabled cookies, etc.) —
    // fall back to the default rather than throwing during app boot.
    return false;
  }
}

/** Pure DOM effect — stamps (or clears) the attribute app.css reads. Safe to
 * call unconditionally on every boot/mount with whatever isReduceMotionEnabled()
 * currently reports; does not touch storage itself. */
export function applyReduceMotion(enabled: boolean): void {
  document.documentElement.dataset.reduceMotion = enabled ? 'true' : 'false';
}

/** Persists the preference and applies it in one step — what the Settings
 * screen's toggle calls on click. */
export function setReduceMotionEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(REDUCE_MOTION_KEY, String(enabled));
  } catch {
    // Still apply for this session even if it can't persist.
  }
  applyReduceMotion(enabled);
}
